import { recordEventWithCheck } from "src/apiService/modules/events";
import type {
  VintedSessionRefreshAbortReason,
  VintedSessionRefreshTriggerReason,
} from "src/interfaces/event";

export function createVintedRefreshRunId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function safeRecord(event: Parameters<typeof recordEventWithCheck>[0]) {
  void recordEventWithCheck(event).catch((err) => {
    console.warn("[Vinted] Failed to record session-refresh event", {
      type: event.type,
      error: (err as Error)?.message,
    });
  });
}

export function recordVintedSessionRefreshTriggered(fields: {
  refreshRunId: string;
  reason: VintedSessionRefreshTriggerReason;
  message?: string;
}) {
  safeRecord({
    type: "vinted-session-refresh-triggered",
    platform: "vinted",
    refreshRunId: fields.refreshRunId,
    reason: fields.reason,
    message:
      fields.message ?? `Vinted session refresh triggered (${fields.reason})`,
  });
}

export function recordVintedSessionRefreshSucceeded(fields: {
  refreshRunId: string;
  viaPopup: boolean;
  reason?: VintedSessionRefreshTriggerReason;
  durationMs?: number;
  message?: string;
}) {
  safeRecord({
    type: "vinted-session-refresh-succeeded",
    platform: "vinted",
    refreshRunId: fields.refreshRunId,
    viaPopup: fields.viaPopup,
    ...(fields.reason != null ? { reason: fields.reason } : {}),
    ...(fields.durationMs != null ? { durationMs: fields.durationMs } : {}),
    message:
      fields.message ??
      (fields.viaPopup
        ? "Vinted session refresh succeeded (popup)"
        : "Vinted session warm-up succeeded (homepage ok)"),
  });
}

export function recordVintedSessionRefreshAborted(fields: {
  refreshRunId: string;
  reason: VintedSessionRefreshAbortReason;
  triggerReason?: VintedSessionRefreshTriggerReason;
  durationMs?: number;
  error?: unknown;
  message?: string;
}) {
  const err = fields.error as Error | undefined;
  safeRecord({
    type: "vinted-session-refresh-aborted",
    platform: "vinted",
    refreshRunId: fields.refreshRunId,
    reason: fields.reason,
    ...(fields.triggerReason != null
      ? { triggerReason: fields.triggerReason }
      : {}),
    ...(fields.durationMs != null ? { durationMs: fields.durationMs } : {}),
    ...(err?.name != null ? { errorName: err.name } : {}),
    message:
      fields.message ??
      err?.message ??
      `Vinted session refresh aborted (${fields.reason})`,
  });
}
